import { watch } from 'vue'
import { useRoute } from 'vue-router'

import { reportPageByRoute, useReportAccess } from '@/features/reports'
import { useEnv } from '@/shared/config'
import { useI18n } from '@/shared/i18n'

// Ports the ONLY live behaviour of v3 `Breadcrumbs.vue`: the `document.title` side-effect
// (contract 05 §9, analysis §7 — the component itself was `!hidden` and is deliberately not
// ported, see contract 05 §1). Legacy derived the page name from `formatText(route.name)` with
// three route-name special-cases; v4 replaces that string-casing with an i18n key on the route
// (`meta.titleKey`), so the special-cases become plain translation keys owned by the routes.
//
// The report-name override (ORP-19914) reproduces v3's
// `reports.find(r => r.url === route.name)?.name || formatText(route.name)`: a report page is
// titled with the REPORT's name, falling back to the route's `titleKey`. `r.url` was the route
// name (`reportStore.ts:260`), which here is `reportPageByRoute()`; the id → name half is 08's
// `useReportAccess`. Report names hydrate at bootstrap, after the first title write, so the whole
// resolution is the watch SOURCE rather than its body — that way a late hydration retitles the
// page instead of leaving the generic key on screen.
export function usePageTitle(): void {
  const route = useRoute()
  const { t } = useI18n()
  const appName = useEnv().APP_NAME
  const reports = useReportAccess()

  /** v3 matched the first report claiming this route; a page can gate on several ids, so take the
   *  first that actually resolves. Returns '' when this is not a report route. */
  function reportName(): string {
    const routeName = route.name
    if (typeof routeName !== 'string') return ''
    const page = reportPageByRoute(routeName)
    if (page === null) return ''
    for (const reportId of page.reportIds) {
      const name = reports.reportById(reportId)?.name
      if (typeof name === 'string' && name.trim().length > 0) return name
    }
    return ''
  }

  function resolveTitle(): string {
    const titleKey = route.meta.titleKey
    const fromKey = typeof titleKey === 'string' && titleKey.length > 0 ? t(titleKey) : ''
    const pageName = reportName() || fromKey
    return pageName.length > 0 ? `${pageName} - ${appName}` : appName
  }

  watch(
    () => resolveTitle(),
    (title) => {
      document.title = title
    },
    { immediate: true },
  )
}
